import React, { useState, useMemo } from 'react';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

interface APIRequest {
  id: string;
  timestamp: string;
  method: string;
  url: string;
  headers: Record<string, string>;
  body?: any;
  duration?: number;
  status?: number;
  response?: any;
  responseHeaders?: Record<string, string>;
}

interface DomainState {
  domains: any[];
  dnsRecords: Record<string, any[]>;
  verificationStatus: Record<string, string>;
  pendingOperations: string[];
}

interface PerformanceMetrics {
  responseTimeHistory: { timestamp: string; duration: number; endpoint: string }[];
  successRate: number;
  averageResponseTime: number;
  slowestEndpoints: { endpoint: string; avgTime: number }[];
}

interface DomainDebugPanelProps {
  activityLog: APIRequest[];
  domainState?: DomainState;
  performanceMetrics?: PerformanceMetrics;
}

const DomainDebugPanel: React.FC<DomainDebugPanelProps> = ({
  activityLog,
  domainState,
  performanceMetrics
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState<'api' | 'state' | 'performance'>('api');
  const [filterMethod, setFilterMethod] = useState<string>('ALL');
  const [searchTerm, setSearchTerm] = useState('');
  const [isPaused, setIsPaused] = useState(false);
  const [expandedRequests, setExpandedRequests] = useState<Set<string>>(new Set());

  // Filter and search activity log
  const filteredLog = useMemo(() => {
    if (isPaused) return activityLog;

    return activityLog.filter(request => {
      const methodMatch = filterMethod === 'ALL' || request.method === filterMethod;
      const searchMatch = searchTerm === '' ||
        JSON.stringify(request).toLowerCase().includes(searchTerm.toLowerCase());
      return methodMatch && searchMatch;
    });
  }, [activityLog, filterMethod, searchTerm, isPaused]);

  const copyToClipboard = (data: any) => {
    navigator.clipboard.writeText(JSON.stringify(data, null, 2));
  };

  const exportDebugData = () => {
    const debugData = {
      activityLog,
      domainState,
      performanceMetrics,
      timestamp: new Date().toISOString()
    };
    const blob = new Blob([JSON.stringify(debugData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `domain-debug-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const getStatusColor = (status?: number) => {
    if (!status) return 'text-gray-400';
    if (status >= 200 && status < 300) return 'text-green-400';
    if (status >= 400 && status < 500) return 'text-yellow-400';
    if (status >= 500) return 'text-red-400';
    return 'text-gray-400';
  };

  const toggleRequestExpansion = (requestId: string) => {
    const newExpanded = new Set(expandedRequests);
    if (newExpanded.has(requestId)) {
      newExpanded.delete(requestId);
    } else {
      newExpanded.add(requestId);
    }
    setExpandedRequests(newExpanded);
  };

  const formatJSON = (data: any) => {
    if (!data) return 'null';
    return JSON.stringify(data, null, 2);
  };

  if (!isExpanded) {
    return (
      <div className="fixed bottom-4 right-4 z-50">
        <button
          onClick={() => setIsExpanded(true)}
          className="bg-gray-800 hover:bg-gray-700 text-white px-4 py-2 rounded-lg shadow-lg border border-gray-600 transition-colors"
        >
          Debug Panel ({activityLog.length})
        </button>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-75 flex items-center justify-center p-4">
      <div className="bg-gray-900 rounded-lg shadow-2xl w-full max-w-6xl h-5/6 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <h2 className="text-xl font-semibold text-white">Domain Debug Panel</h2>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setIsPaused(!isPaused)}
              className={`px-3 py-1 rounded text-sm ${
                isPaused
                  ? 'bg-yellow-600 hover:bg-yellow-700 text-white'
                  : 'bg-green-600 hover:bg-green-700 text-white'
              }`}
            >
              {isPaused ? 'Resume' : 'Pause'}
            </button>
            <button
              onClick={exportDebugData}
              className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-sm"
            >
              Export
            </button>
            <button
              onClick={() => setIsExpanded(false)}
              className="bg-gray-600 hover:bg-gray-700 text-white px-3 py-1 rounded text-sm"
            >
              Close
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-700">
          {(['api', 'state', 'performance'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 font-medium capitalize ${
                activeTab === tab
                  ? 'bg-gray-800 text-white border-b-2 border-blue-500'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              {tab} {tab === 'api' && `(${filteredLog.length})`}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden">
          {activeTab === 'api' && (
            <div className="h-full flex flex-col">
              {/* Filters */}
              <div className="p-4 border-b border-gray-700 flex items-center space-x-4">
                <select
                  value={filterMethod}
                  onChange={(e) => setFilterMethod(e.target.value)}
                  className="bg-gray-800 text-white border border-gray-600 rounded px-3 py-1"
                >
                  <option value="ALL">All Methods</option>
                  <option value="GET">GET</option>
                  <option value="POST">POST</option>
                  <option value="PUT">PUT</option>
                  <option value="DELETE">DELETE</option>
                </select>
                <input
                  type="text"
                  placeholder="Search requests..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="bg-gray-800 text-white border border-gray-600 rounded px-3 py-1 flex-1"
                />
                <button
                  onClick={() => {
                    setFilterMethod('ALL');
                    setSearchTerm('');
                    setExpandedRequests(new Set());
                  }}
                  className="bg-gray-600 hover:bg-gray-700 text-white px-3 py-1 rounded text-sm"
                >
                  Clear
                </button>
              </div>

              {/* API Log */}
              <div className="flex-1 overflow-y-auto p-4 space-y-2">
                {filteredLog.map((request) => (
                  <div key={request.id} className="bg-gray-800 rounded border border-gray-700">
                    <div
                      className="p-3 cursor-pointer hover:bg-gray-750"
                      onClick={() => toggleRequestExpansion(request.id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          {expandedRequests.has(request.id) ? (
                            <ChevronDownIcon className="w-4 h-4 text-gray-400" />
                          ) : (
                            <ChevronRightIcon className="w-4 h-4 text-gray-400" />
                          )}
                          <span className={`font-mono text-sm font-medium ${
                            request.method === 'GET' ? 'text-blue-400' :
                            request.method === 'POST' ? 'text-green-400' :
                            request.method === 'PUT' ? 'text-yellow-400' :
                            request.method === 'DELETE' ? 'text-red-400' :
                            'text-gray-400'
                          }`}>
                            {request.method}
                          </span>
                          <span className="text-white font-mono text-sm">{request.url}</span>
                          {request.status && (
                            <span className={`font-mono text-sm ${getStatusColor(request.status)}`}>
                              {request.status}
                            </span>
                          )}
                          {request.duration && (
                            <span className="text-gray-400 text-sm">{request.duration}ms</span>
                          )}
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="text-gray-400 text-xs">
                            {new Date(request.timestamp).toLocaleTimeString()}
                          </span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              copyToClipboard(request);
                            }}
                            className="text-gray-400 hover:text-white text-xs"
                          >
                            Copy
                          </button>
                        </div>
                      </div>
                    </div>

                    {expandedRequests.has(request.id) && (
                      <div className="border-t border-gray-700 p-3 space-y-3">
                        {/* Request Details */}
                        <div>
                          <h4 className="text-white font-medium mb-2">Request</h4>
                          <div className="bg-gray-900 rounded p-2 font-mono text-xs">
                            <div className="text-gray-400 mb-1">Headers:</div>
                            <pre className="text-green-400 whitespace-pre-wrap">
                              {formatJSON(request.headers)}
                            </pre>
                            {request.body && (
                              <>
                                <div className="text-gray-400 mt-2 mb-1">Body:</div>
                                <pre className="text-blue-400 whitespace-pre-wrap">
                                  {formatJSON(request.body)}
                                </pre>
                              </>
                            )}
                          </div>
                        </div>

                        {/* Response Details */}
                        {request.response && (
                          <div>
                            <h4 className="text-white font-medium mb-2">Response</h4>
                            <div className="bg-gray-900 rounded p-2 font-mono text-xs">
                              {request.responseHeaders && (
                                <>
                                  <div className="text-gray-400 mb-1">Headers:</div>
                                  <pre className="text-green-400 whitespace-pre-wrap mb-2">
                                    {formatJSON(request.responseHeaders)}
                                  </pre>
                                </>
                              )}
                              <div className="text-gray-400 mb-1">Body:</div>
                              <pre className={`whitespace-pre-wrap ${getStatusColor(request.status)}`}>
                                {formatJSON(request.response)}
                              </pre>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
                {filteredLog.length === 0 && (
                  <div className="text-center text-gray-400 py-8">
                    No API requests found
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'state' && (
            <div className="h-full overflow-y-auto p-4">
              <div className="space-y-4">
                {domainState ? (
                  <>
                    <div className="bg-gray-800 rounded p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-white font-medium">Domains ({domainState.domains.length})</h3>
                        <button
                          onClick={() => copyToClipboard(domainState.domains)}
                          className="text-gray-400 hover:text-white text-sm"
                        >
                          Copy
                        </button>
                      </div>
                      <pre className="bg-gray-900 rounded p-2 text-green-400 text-xs overflow-x-auto">
                        {formatJSON(domainState.domains)}
                      </pre>
                    </div>

                    <div className="bg-gray-800 rounded p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-white font-medium">DNS Records</h3>
                        <button
                          onClick={() => copyToClipboard(domainState.dnsRecords)}
                          className="text-gray-400 hover:text-white text-sm"
                        >
                          Copy
                        </button>
                      </div>
                      <pre className="bg-gray-900 rounded p-2 text-blue-400 text-xs overflow-x-auto">
                        {formatJSON(domainState.dnsRecords)}
                      </pre>
                    </div>

                    <div className="bg-gray-800 rounded p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-white font-medium">Verification Status</h3>
                        <button
                          onClick={() => copyToClipboard(domainState.verificationStatus)}
                          className="text-gray-400 hover:text-white text-sm"
                        >
                          Copy
                        </button>
                      </div>
                      <pre className="bg-gray-900 rounded p-2 text-yellow-400 text-xs overflow-x-auto">
                        {formatJSON(domainState.verificationStatus)}
                      </pre>
                    </div>

                    <div className="bg-gray-800 rounded p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-white font-medium">Pending Operations ({domainState.pendingOperations.length})</h3>
                        <button
                          onClick={() => copyToClipboard(domainState.pendingOperations)}
                          className="text-gray-400 hover:text-white text-sm"
                        >
                          Copy
                        </button>
                      </div>
                      <pre className="bg-gray-900 rounded p-2 text-orange-400 text-xs overflow-x-auto">
                        {formatJSON(domainState.pendingOperations)}
                      </pre>
                    </div>
                  </>
                ) : (
                  <div className="text-center text-gray-400 py-8">
                    No state data available
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'performance' && (
            <div className="h-full overflow-y-auto p-4">
              <div className="space-y-4">
                {performanceMetrics ? (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="bg-gray-800 rounded p-4">
                        <h3 className="text-white font-medium mb-2">Success Rate</h3>
                        <div className="text-2xl font-bold text-green-400">
                          {(performanceMetrics.successRate * 100).toFixed(1)}%
                        </div>
                      </div>
                      <div className="bg-gray-800 rounded p-4">
                        <h3 className="text-white font-medium mb-2">Avg Response Time</h3>
                        <div className="text-2xl font-bold text-blue-400">
                          {performanceMetrics.averageResponseTime.toFixed(0)}ms
                        </div>
                      </div>
                      <div className="bg-gray-800 rounded p-4">
                        <h3 className="text-white font-medium mb-2">Total Requests</h3>
                        <div className="text-2xl font-bold text-purple-400">
                          {performanceMetrics.responseTimeHistory.length}
                        </div>
                      </div>
                    </div>

                    <div className="bg-gray-800 rounded p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-white font-medium">Slowest Endpoints</h3>
                        <button
                          onClick={() => copyToClipboard(performanceMetrics.slowestEndpoints)}
                          className="text-gray-400 hover:text-white text-sm"
                        >
                          Copy
                        </button>
                      </div>
                      <div className="space-y-2">
                        {performanceMetrics.slowestEndpoints.map((endpoint, index) => (
                          <div key={index} className="flex items-center justify-between bg-gray-900 rounded p-2">
                            <span className="text-white font-mono text-sm">{endpoint.endpoint}</span>
                            <span className="text-red-400 font-medium">{endpoint.avgTime.toFixed(0)}ms</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="bg-gray-800 rounded p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-white font-medium">Response Time History</h3>
                        <button
                          onClick={() => copyToClipboard(performanceMetrics.responseTimeHistory)}
                          className="text-gray-400 hover:text-white text-sm"
                        >
                          Copy
                        </button>
                      </div>
                      <div className="bg-gray-900 rounded p-2 max-h-64 overflow-y-auto">
                        <div className="space-y-1">
                          {performanceMetrics.responseTimeHistory.slice(-50).map((entry, index) => (
                            <div key={index} className="flex items-center justify-between text-xs">
                              <span className="text-gray-400">
                                {new Date(entry.timestamp).toLocaleTimeString()}
                              </span>
                              <span className="text-white font-mono">{entry.endpoint}</span>
                              <span className={`font-medium ${
                                entry.duration < 100 ? 'text-green-400' :
                                entry.duration < 500 ? 'text-yellow-400' :
                                'text-red-400'
                              }`}>
                                {entry.duration}ms
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="text-center text-gray-400 py-8">
                    No performance data available
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DomainDebugPanel;